import { useCallback, useMemo, useState } from 'react';

import { toast } from 'react-toastify';
import { useShallow } from 'zustand/react/shallow';

import { useRoomContext } from '@src/contexts';
import SocketService from '@src/services/SocketService';

import {
  useChatStore,
  useDrawerStore,
  useNotificationStore,
  usePeersStore,
  useRoomStore,
  useVideoStreamStore,
} from '@src/stores';

import { INFO_MODAL_ICONS } from '@src/constants';

import { ECommonSocketKeys, EMeetingType, EMessageType, EUserType, IPeer, TPeerAction, TUserType } from '@src/types';

interface IProps {
  participant: IPeer;
}

const useActionsMenu = ({ participant }: IProps) => {
  const [isMenuPopperOpened, setIsMenuPopperOpened] = useState<boolean>(false);

  const isTargetHost = participant.peer_info.user_type?.toLowerCase() === 'host';

  const {
    peerAction,
    isPublicChatEnabled,
    isPrivateChatEnabled,
    canUserBan,
    canUserEject,
    canUserAskToUnmute,
    currentPeer,
  } = useRoomContext();

  const { isHostUser } = usePeersStore(useShallow((state) => ({ isHostUser: state.isHostUser })));

  const { videoStreams, updateVideo } = useVideoStreamStore(
    useShallow((state) => ({ videoStreams: state.videoStreams, updateVideo: state.updateVideo }))
  );

  const { setSelectedReceiverData } = useChatStore(
    useShallow((state) => ({ setSelectedReceiverData: state.setSelectedReceiverData }))
  );

  const { setSelectedKey } = useDrawerStore(useShallow((state) => ({ setSelectedKey: state.setSelectedKey })));

  const { appendNotification } = useNotificationStore(
    useShallow((state) => ({ appendNotification: state.appendNotification }))
  );

  const { updatePeerInfo } = usePeersStore(useShallow((state) => ({ updatePeerInfo: state.updatePeerInfo })));

  const { setInfoModalContent, setIsInfoModalOpen, requestInfo, removeRequestInfoByPeerId, meetingType } = useRoomStore(
    useShallow((state) => ({
      requestInfo: state.requestInfo,
      setIsInfoModalOpen: state.setIsInfoModalOpen,
      setInfoModalContent: state.setInfoModalContent,
      removeRequestInfoByPeerId: state.removeRequestInfoByPeerId,
      meetingType: state.meetingType,
    }))
  );

  const currentParticipantVideo = useMemo(
    () => videoStreams.find(({ peerId }) => peerId === participant.peer_info.peer_id),
    [participant.peer_info.peer_id, videoStreams]
  );

  const isVideoPinned = useMemo(() => !!currentParticipantVideo?.isPinned, [currentParticipantVideo?.isPinned]);

  const isParticipantSharingScreen = useMemo(
    () =>
      !!videoStreams.find(({ type, peerId }) => type === 'screenType' && peerId === participant?.peer_info?.peer_id),
    [participant?.peer_info?.peer_id, videoStreams]
  );

  const handlePinVideo = useCallback(() => {
    if (!currentParticipantVideo) return;
    const currentPinnedVideo = videoStreams.find(({ isPinned }) => !!isPinned);
    if (currentPinnedVideo) updateVideo(currentPinnedVideo.id, { isPinned: false });
    updateVideo(currentParticipantVideo.id, { isPinned: !currentParticipantVideo.isPinned });
  }, [currentParticipantVideo, videoStreams, updateVideo]);

  const handleOpenChat = useCallback(() => {
    setSelectedReceiverData({ key: EMessageType.PRIVATE, value: participant.peer_info.peer_id });
    setSelectedKey('chat');
  }, [participant.peer_info.peer_id, setSelectedKey, setSelectedReceiverData]);

  const handleParticipantAction = useCallback(
    (action: TPeerAction) =>
      peerAction({ from_peer_name: 'me', id: participant.peer_info.peer_id, action, emit: true }),
    [participant.peer_info.peer_id, peerAction]
  );

  const handleParticipantRole = useCallback(
    (action: TPeerAction, userType: TUserType, isRemove: boolean, roleLabel: string, record_id: string | undefined) => {
      const peerActionData = {
        action,
        user_type: userType,
        peer_id: participant.peer_info.peer_id,
        record_id,
      };

      SocketService.request(ECommonSocketKeys.PEER_ACTION, peerActionData)
        .then((data: { success: boolean }) => {
          if (data.success) {
            appendNotification({
              title: '',
              text: [EUserType.ATTENDEE, EUserType.SPEAKER].includes(userType)
                ? `${participant.peer_info.peer_name} is no longer a ${roleLabel}`
                : `${participant.peer_info.peer_name} is now ${roleLabel}`,
              source: 'user-type',
            });

            if (userType === EUserType.CO_HOST) {
              requestInfo.forEach(({ peerId, requestId }) => {
                if (peerActionData.peer_id === peerId) {
                  toast.dismiss(requestId);
                  removeRequestInfoByPeerId(peerId);
                }
              });
            }

            updatePeerInfo(participant.peer_info.peer_id, { user_type: userType });
          } else {
            appendNotification({
              title: 'Action Failed',
              text: `Unable to change role for ${participant.peer_info.peer_name}.`,
              source: 'user-type',
            });
          }
        })
        .catch(() => {
          appendNotification({
            title: 'Network Error',
            text: `Failed to communicate with the server. Try again later.`,
            source: 'user-type',
          });
        });
    },
    [
      appendNotification,
      participant.peer_info.peer_id,
      participant.peer_info.peer_name,
      removeRequestInfoByPeerId,
      requestInfo,
      updatePeerInfo,
    ]
  );

  const handleRoleChange = useCallback(
    (role: EUserType.CO_HOST, roleLabel: string, description: string) => {
      if (participant.peer_info.user_type === role) {
        const fallbackRole = meetingType === EMeetingType.WEBINAR ? EUserType.SPEAKER : EUserType.ATTENDEE;
        handleParticipantRole('updateUserType', fallbackRole, true, roleLabel, participant?.record_id);
        return;
      }

      setInfoModalContent({
        title: `Assign ${participant.peer_info.peer_name} as a ${roleLabel}`,
        description,
        icon: INFO_MODAL_ICONS.CONFIRM_ACTION,
        buttons: [
          {
            text: 'Cancel',
            variant: 'cancel',
            onClick: () => {
              setIsInfoModalOpen(false);
              setInfoModalContent(null);
            },
          },
          {
            text: 'Apply',
            onClick: () => {
              handleParticipantRole('updateUserType', role, false, roleLabel, participant?.record_id);
              setIsInfoModalOpen(false);
              setInfoModalContent(null);
            },
            variant: 'primary',
          },
        ],
      });

      setIsInfoModalOpen(true);
    },
    [handleParticipantRole, participant, setInfoModalContent, setIsInfoModalOpen, meetingType]
  );

  const handleCoHost = useCallback(() => {
    handleRoleChange(
      EUserType.CO_HOST,
      'co-host',
      `This enables them to control peers, share screen, and manage most meeting settings.`
    );
  }, [handleRoleChange]);

  const handleBanParticipant = useCallback(() => {
    setInfoModalContent({
      title: 'Ban User',
      description: 'Are you sure you want to ban this attendee from the meeting?',
      buttons: [
        {
          text: 'Cancel',
          variant: 'cancel',
          onClick: () => {
            setIsInfoModalOpen(false);
            setInfoModalContent(null);
          },
        },
        {
          text: 'Ban',
          onClick: () => {
            handleParticipantAction('ban');
            setIsInfoModalOpen(false);
            setInfoModalContent(null);
          },
          variant: 'primary',
        },
      ],
    });

    setIsInfoModalOpen(true);
  }, [handleParticipantAction, setInfoModalContent, setIsInfoModalOpen]);

  const isCurrentUserCoHost = currentPeer?.peer_info?.user_type === EUserType.CO_HOST;

  return {
    isVideoPinned,
    isMenuPopperOpened,
    isParticipantSharingScreen,
    isPrivateChatEnabled,
    isPublicChatEnabled,
    isTargetHost,
    canUserAskToUnmute: !isTargetHost && canUserAskToUnmute,
    canUserEject: !isTargetHost && (isHostUser || canUserEject),
    canUserBan: !isCurrentUserCoHost && !isTargetHost && (isHostUser || canUserBan),

    handlePinVideo,
    handleOpenChat,
    setIsMenuPopperOpened,
    handleParticipantAction,
    handleParticipantRole,
    handleCoHost,
    handleBanParticipant,

    isHostUser,
    canAssignCohost: isHostUser && !isTargetHost,
  };
};

export default useActionsMenu;
